import { useState, useCallback } from "react";
import { fetchData } from "../fetchData";
import { useSnackbar } from "../contexts/SnackbarContext";
import { RoomReservation, ReservationFormData } from "../types/rooms";

export const useRoomReservations = () => {
  const snackbar = useSnackbar();
  const [reservations, setReservations] = useState<RoomReservation[]>([]);
  const [loading, setLoading] = useState(true);

  const loadReservations = useCallback(
    async (targetDate?: Date) => {
      try {
        setLoading(true);
        const dateToUse = targetDate || new Date();
        const startDate = new Date(
          dateToUse.getFullYear(),
          dateToUse.getMonth(),
          1,
        );
        const endDate = new Date(
          dateToUse.getFullYear(),
          dateToUse.getMonth() + 1,
          0,
        );

        const result = await fetchData(
          `/room-reservations?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`,
          { method: "GET" },
        );
        setReservations(result || []);
      } catch (error) {
        console.error("Erro ao carregar reservas:", error);
        snackbar?.open(
          "Ops! No momento não foi possível carregar as reservas.",
          "error",
        );
        setReservations([]);
      } finally {
        setLoading(false);
      }
    },
    [snackbar],
  );

  const createReservation = useCallback(
    async (data: ReservationFormData) => {
      try {
        await fetchData("/room-reservations", {
          method: "POST",
          body: JSON.stringify(data),
        });
        snackbar?.open("Reserva criada com sucesso!", "success");
        return true;
      } catch (error) {
        console.error("Erro ao criar reserva:", error);
        snackbar?.open(
          "Ops! No momento não foi possível salvar a reserva.",
          "error",
        );
        return false;
      }
    },
    [snackbar],
  );

  const updateReservation = useCallback(
    async (id: string, data: ReservationFormData) => {
      try {
        await fetchData(`/room-reservations/${id}`, {
          method: "PATCH",
          body: JSON.stringify(data),
        });
        snackbar?.open("Reserva atualizada com sucesso!", "success");
        return true;
      } catch (error) {
        console.error("Erro ao atualizar reserva:", error);
        snackbar?.open(
          "Ops! No momento não foi possível salvar a reserva.",
          "error",
        );
        return false;
      }
    },
    [snackbar],
  );

  const deleteReservation = useCallback(
    async (id: string) => {
      try {
        const response = await fetchData(
          `/room-reservations/${id}`,
          {
            method: "DELETE",
          },
          false,
        );

        // Verificar se a resposta foi bem-sucedida
        if (response && response.ok) {
          snackbar?.open("Reserva cancelada com sucesso!", "success");
          return true;
        } else {
          throw new Error("Falha ao cancelar reserva");
        }
      } catch (error) {
        console.error("Erro ao cancelar reserva:", error);
        snackbar?.open(
          "Ops! No momento não foi possível cancelar a reserva.",
          "error",
        );
        return false;
      }
    },
    [snackbar],
  );

  const checkTimeConflict = useCallback(
    async (
      startTime: string,
      endTime: string,
      room: string,
      excludeId?: string,
    ) => {
      try {
        const result = await fetchData(
          `/room-reservations/check-conflict?startTime=${startTime}&endTime=${endTime}&room=${room}${
            excludeId ? `&excludeId=${excludeId}` : ""
          }`,
          { method: "GET" },
        );
        return result?.conflict || null;
      } catch (error) {
        console.error("Erro ao verificar conflito:", error);
        return null;
      }
    },
    [],
  );

  return {
    reservations,
    loading,
    loadReservations,
    createReservation,
    updateReservation,
    deleteReservation,
    checkTimeConflict,
  };
};
